import config from "../config";
import Labels from "../services/apiLabels";
import {
  getOfficeCompanyName,
  getOfficeDealerId,
} from "./appointmentBooking";
import { getAuthToken } from "./userIdentity";

const companyCache = new Map();
const inflight = new Map();
let officeMetaPromise = null;
let officeMetaCache = null;

function apiBase() {
  return String(config.baseUrl || "").replace(/\/?$/, "/");
}

function authHeaders(token) {
  if (!token) return { Accept: "application/json" };
  return {
    Accept: "application/json",
    Authorization: `Token ${token}`,
  };
}

function listFromPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.offices)) return payload.offices;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  if (Array.isArray(payload?.data?.offices)) return payload.data.offices;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function unwrapOfficeRow(item) {
  if (!item || typeof item !== "object") return item;
  let row = item;
  if (item.properties && typeof item.properties === "object") {
    row = { id: item.id ?? item.pk, ...item.properties, ...item };
  }
  if (row.office && typeof row.office === "object" && !Array.isArray(row.office)) {
    row = {
      ...row.office,
      ...row,
      id: row.id ?? row.office.id ?? row.office.pk,
      dealer: row.dealer ?? row.office.dealer,
      dealerInfo: row.dealerInfo ?? row.office.dealerInfo,
    };
  }
  if (row.data && typeof row.data === "object" && !Array.isArray(row.data)) {
    row = { ...row, ...row.data };
  }
  return row;
}

function officeKey(office) {
  const id = office?.id ?? office?.pk ?? office?.office_id;
  return id == null || id === "" ? "" : String(id);
}

function formatDisplayLabel(office, company) {
  const address =
    office?.address || office?.Address || office?.Area || office?.area || "";
  if (address && company) return `${address} - ${company}`;
  return address || company || "";
}

/** Normalize dealerInfo onto company_name for dropdown / labels. */
function withDealerInfoFields(office) {
  if (!office || typeof office !== "object") return office;
  const company = getOfficeCompanyName(office);
  const dealerId = getOfficeDealerId(office);
  const next = { ...office };
  if (company) {
    next.company_name = company;
  }
  if (dealerId != null && dealerId !== "" && (next.dealer == null || next.dealer === "")) {
    next.dealer = dealerId;
  }
  next.displayLabel = formatDisplayLabel(next, company);
  return next;
}

export async function fetchDealerCompanyName(dealerId) {
  const id = dealerId == null || dealerId === "" ? "" : String(dealerId);
  if (!id) return "";

  if (companyCache.has(id)) {
    return companyCache.get(id) || "";
  }

  if (inflight.has(id)) {
    return inflight.get(id);
  }

  const request = (async () => {
    try {
      const response = await fetch(`${apiBase()}api/getUpdateUser/${id}/`);
      if (!response.ok) {
        companyCache.set(id, "");
        return "";
      }
      const json = await response.json();
      const user = json?.data?.user || json?.data || json?.user || json;
      const name = String(user?.company_name || user?.Company_name || "").trim();
      companyCache.set(id, name);
      return name;
    } catch {
      companyCache.set(id, "");
      return "";
    } finally {
      inflight.delete(id);
    }
  })();

  inflight.set(id, request);
  return request;
}

/**
 * office_listing (auth required) returns full Office rows including `dealer`.
 * Clients typically get an empty list; dealers get their own offices.
 * Match by id and by address so we can still attach dealer when ids differ.
 */
async function buildOfficeDealerMeta(offices, token) {
  if (officeMetaCache) {
    return officeMetaCache;
  }
  if (officeMetaPromise) {
    return officeMetaPromise;
  }

  officeMetaPromise = (async () => {
    const byId = {};
    const byAddress = {};

    if (token) {
      try {
        const response = await fetch(`${apiBase()}${Labels.office_listing}`, {
          headers: authHeaders(token),
        });
        if (response.ok) {
          const json = await response.json();
          listFromPayload(json)
            .map(unwrapOfficeRow)
            .forEach((row) => {
              const id = officeKey(row);
              const meta = {
                dealer: getOfficeDealerId(row),
                company_name: getOfficeCompanyName(row) || undefined,
              };
              if (id) byId[id] = meta;
              const address = String(
                row.address || row.Address || ""
              )
                .trim()
                .toLowerCase();
              if (address) byAddress[address] = meta;
            });
        }
      } catch {
        /* ignore */
      }
    }

    const map = { byId, byAddress };
    if (Object.keys(byId).length > 0 || Object.keys(byAddress).length > 0) {
      officeMetaCache = map;
    }
    return map;
  })();

  try {
    return await officeMetaPromise;
  } finally {
    officeMetaPromise = null;
  }
}

function resolveMeta(office, dealerMeta) {
  const id = officeKey(office);
  if (id && dealerMeta?.byId?.[id]) {
    return dealerMeta.byId[id];
  }
  const address = String(office?.address || office?.Address || "")
    .trim()
    .toLowerCase();
  if (address && dealerMeta?.byAddress?.[address]) {
    return dealerMeta.byAddress[address];
  }
  return null;
}

/**
 * Prefer dealerInfo.companyName from officetiming. Fall back to office_listing /
 * getUpdateUser only when company is still missing.
 */
export async function enrichOfficesWithDealerCompanies(offices, auth) {
  const list = Array.isArray(offices) ? offices : [];
  if (!list.length) return list;

  // Fast path: every office already has dealerInfo.companyName (or equivalent).
  const normalized = list.map(withDealerInfoFields);
  const missingCompany = normalized.filter((office) => !getOfficeCompanyName(office));
  if (!missingCompany.length) {
    return normalized;
  }

  const token = typeof auth === "string" ? auth : getAuthToken(auth);
  const dealerMeta = await buildOfficeDealerMeta(list, token);

  const withDealer = normalized.map((office) => {
    if (getOfficeCompanyName(office)) {
      return office;
    }
    const fromMeta = resolveMeta(office, dealerMeta);
    const dealerId = getOfficeDealerId(office) ?? fromMeta?.dealer ?? null;
    const existingCompany = fromMeta?.company_name || "";

    const next = { ...office };
    if (dealerId != null && dealerId !== "") {
      next.dealer = dealerId;
    }
    if (existingCompany) {
      next.company_name = existingCompany;
      next.displayLabel = formatDisplayLabel(next, existingCompany);
    }
    return next;
  });

  const ids = [
    ...new Set(
      withDealer
        .filter((office) => !getOfficeCompanyName(office))
        .map((office) => getOfficeDealerId(office))
        .filter((id) => id != null && id !== "")
        .map(String)
    ),
  ];

  if (ids.length) {
    await Promise.all(ids.map((id) => fetchDealerCompanyName(id)));
  }

  return withDealer.map((office) => {
    const existing = getOfficeCompanyName(office);
    if (existing) {
      return {
        ...office,
        company_name: existing,
        displayLabel: formatDisplayLabel(office, existing),
      };
    }
    const dealerId = getOfficeDealerId(office);
    const company =
      dealerId != null && dealerId !== ""
        ? companyCache.get(String(dealerId)) || ""
        : "";
    if (!company) {
      return {
        ...office,
        displayLabel: formatDisplayLabel(office, ""),
      };
    }
    return {
      ...office,
      company_name: company,
      displayLabel: formatDisplayLabel(office, company),
    };
  });
}
